import React, { useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Col,
  UiCenter,
  UiImage,
  UiPageLoading,
  UiPrimaryButton,
  UiText,
  useBreakpointValue,
} from "~/components/elements";
import { PageLayout } from "~/components/layouts/PageLayout";
import { useAppDispatch, useAppSelector, useRequestState } from "~/hooks";
import { DashboardMainBox } from "~/components/shared/DashboardMainBox";
import closeVote from "~/assets/img/v2/packages/tick-close-fill.png";
import { fetchSharedJob } from "~/redux/dashboards/thunk";
import { capitalizeFirstLetter } from "~/utils/helpers";
import { TrackedSharedImagesGallery } from "~/pages/shared/VotingGallery";
import { PAGE_URLS, PAGES } from "~/constants";
import config from "~/config/app-config";
import { useSearchParams } from "~/utils/helpers/react-router-helper";

type WindowWithDataLayer = Window & {
  dataLayer: Record<string, any>[];
};

declare const window: WindowWithDataLayer;

export default function VotingPage() {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { loading } = useRequestState(fetchSharedJob);

  const params: { slugId: string } = useParams();
  const { slugId } = params;
  const sharedJobData = useAppSelector(state => state.dashboard.sharedJobData);

  const vrTitle = useBreakpointValue<any>({ base: "headline3", sm: "headline2" });

  const [searchParams, setSearchParams] = useSearchParams();
  const utmSource = searchParams.get("utm_source");
  const utmMedium = searchParams.get("utm_medium");
  const utmCampaign = searchParams.get("utm_campaign");

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchSharedJob({ slug: slugId }));
    };
    if (slugId) {
      fetchData();
    }
  }, [dispatch, slugId]);

  useEffect(() => {
    if (!utmSource) {
      if (sharedJobData?.slug) {
        const redirectToPath = `/l${PAGES.VOTES}/${sharedJobData?.slug}?utm_source=voting&utm_medium=${sharedJobData?.user_id}&utm_campaign=${sharedJobData?.id}`;
        history.push(redirectToPath);
      }
    }
  }, [history, sharedJobData?.id, sharedJobData?.slug, sharedJobData?.user_id, utmCampaign, utmMedium, utmSource]);

  const ownerName = capitalizeFirstLetter(sharedJobData?.attributes?.key_features?.name) || "Your friend";
  const logo = `${config.url}/logo.png`;

  return loading && !utmSource ? (
    <UiPageLoading />
  ) : (
    <Col minH="100dvh">
      <PageLayout
        metaTagsConfig={{
          metas: [
            { name: "robots", content: "noindex,nofollow" },
            {
              property: "og:title",
              content: `Professional AI photoshoots for individuals and teams`,
            },
            {
              property: "og:description",
              content: `Vote for your favorites! Let ${ownerName} know which photoshoots you like the most.`,
            },
            { property: "og:type", content: "website" },
            { property: "og:image", content: logo },
            { property: "og:url", content: `${config.url}/s${PAGES.VOTES}/${sharedJobData?.slug}` },
          ],
        }}
        as={Col}
        flex={1}
      >
        <Col flex={1} justify="center">
          <UiCenter p={4}>
            <DashboardMainBox>
              <Col w="full" py={10}>
                <UiText vr={vrTitle} textAlign="center" mt={8}>
                  {ownerName}&apos;s Bettershot Photoshoots
                </UiText>
                <UiText vr="body1" textAlign="center" mt={3} mb={8}>
                  Vote for your favorites! Let {ownerName} know which photoshoots you like the most.
                </UiText>
                {sharedJobData?.result_images?.length && (
                  <TrackedSharedImagesGallery
                    /* @ts-ignore */
                    images={sharedJobData?.result_images || []}
                    loading={loading}
                    emptyMessage="This content is not available"
                  />
                )}
                {!loading && !sharedJobData?.result_images?.length && (
                  <UiCenter flexDirection="column">
                    <UiCenter flexDirection="column">
                      <UiImage src={closeVote} w="4rem" />
                    </UiCenter>
                    <UiText vr={vrTitle} textAlign="center" mt={3}>
                      Oh no, your friend has closed this vote!
                    </UiText>
                    <UiText vr="body1" textAlign="center" mt={3} mb={5}>
                      Unfortunately, this vote has been closed. You can use this link to vote once your friend resumes
                      it.
                    </UiText>
                  </UiCenter>
                )}
                <UiText vr="body1" mt={8} textAlign="center" color="neutral.gray900">
                  Do you want great photoshoots for yourself?
                </UiText>
                <UiPrimaryButton
                  mt="3"
                  maxW={{ base: undefined, md: "33rem" }}
                  w="full"
                  mx="auto"
                  fontSize={{ base: "0.875rem", sm: "1rem" }}
                  whiteSpace="normal"
                  lineHeight="20px"
                  h="auto"
                  py={3}
                  onClick={() => {
                    window.open(PAGE_URLS.REGISTER, "_blank");
                  }}
                >
                  Click here to sign up for Bettershot!
                </UiPrimaryButton>
              </Col>
            </DashboardMainBox>
          </UiCenter>
        </Col>
      </PageLayout>
    </Col>
  );
}
